import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { tpoGenUrls } from 'config/urls';
import { tpoGenKeys, TPO_GEN_DIM_OPTIONS_QUERY_KEY } from '../../../utils/tpoGenQueryKeys';
import { sendNotification } from 'utils/helperMethods';

export interface CreateEventPayload {
    plan_id: string;
    event_id?: string | null;
    lowest_dim_attr_id: number[];
    [key: string]: unknown;
}

const createOrUpdateEvent = async (payload: CreateEventPayload & { created_by: string }) => {
    const token = localStorage.getItem('accessToken');
    const response = await axios(tpoGenUrls.createOrUpdateEvent, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${token}`,
        },
        data: payload,
    });
    return response.data;
};

const useCreateEventMutation = ({ onSuccess }: { onSuccess?: (data: any) => void } = {}) => {
    const queryClient = useQueryClient();

    const { mutate, isPending } = useMutation({
        mutationFn: (payload: CreateEventPayload) =>
            createOrUpdateEvent({
                ...payload,
                event_id: payload.event_id ?? null,
                created_by: localStorage.getItem('email') ?? '',
            }),
        onSuccess: (_data, variables) => {
            const eventId = variables.event_id;
            if (eventId) {
                queryClient.invalidateQueries({ queryKey: tpoGenKeys.eventDetails(eventId as string) });
                queryClient.invalidateQueries({ queryKey: [TPO_GEN_DIM_OPTIONS_QUERY_KEY, 'product', eventId] });
            }
            queryClient.removeQueries({ queryKey: [...tpoGenKeys.all, 'planning-groups'] });
            queryClient.removeQueries({ queryKey: [...tpoGenKeys.all, 'plan-list'] });
            queryClient.invalidateQueries({ queryKey: ['calendarData'] });
            const isEdit = !!eventId;
            sendNotification('success', isEdit ? 'Event updated successfully' : 'Event created successfully');
            onSuccess?.(_data);
        },
        onError: (error: any) => {
            const message = error?.response?.data?.message || error?.message || 'Failed to save event';
            sendNotification('error', message);
        },
    });

    return { mutate, isPending };
};

export default useCreateEventMutation;
